import { CSSProperties, useEffect, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { inputCls } from '@/components/ui/form';
import { cn } from '@/components/ui/cn';
import { Eye, Grip, Trash } from '@/components/ui/icons';
import { useAppData } from '@/app-data';
import { useWatches } from '@/features/backlog/watches';
import * as api from '@/features/backlog/api';
import { STATUS_PALETTE } from '@/features/backlog/constants';
import { useStatuses } from '@/features/backlog/statuses';
import { Status } from '@/features/backlog/types';
import BoardAccessSection from '@/components/board-access-section';

/**
 * The "cog" board-settings modal. Today it manages the board's statuses (kanban columns):
 * add, rename, recolour, drag-reorder and delete. Deleting reassigns that column's items
 * server-side (to Not Started, else the first remaining column), so it always confirms first.
 */
export function BoardSettingsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { currentBoard, reloadBoards } = useAppData();
  const { statuses, reload } = useStatuses();
  const wc = useWatches();
  const watching = wc && currentBoard ? wc.isWatching('board', currentBoard.id) : false;
  const [newLabel, setNewLabel] = useState('');
  const [busy, setBusy] = useState(false);
  // Local, uppercased draft of the key prefix; committed on blur/Enter. Re-seeds when the board
  // changes (keyed by id below) so it always reflects the current board.
  const [prefix, setPrefix] = useState(currentBoard?.key_prefix ?? '');

  // Normalize like the backend (uppercase, [A-Z0-9] only, capped) so the preview matches reality.
  const normPrefix = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);

  const savePrefix = async () => {
    const next = normPrefix(prefix);
    if (!currentBoard || !next || next === currentBoard.key_prefix) {
      setPrefix(currentBoard?.key_prefix ?? ''); // revert an empty/invalid edit to the saved value
      return;
    }
    setBusy(true);
    try {
      await api.updateBoard(currentBoard.id, {
        name: currentBoard.name,
        position: currentBoard.position,
        key_prefix: next,
      });
      await reloadBoards();
    } finally {
      setBusy(false);
    }
  };
  const [pendingDelete, setPendingDelete] = useState<Status | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  // Synchronous re-entry guard for add: a second Enter can fire before the setBusy(true) re-render
  // rebinds the input, so a state flag alone still lets a duplicate through.
  const addingRef = useRef(false);

  // Keep the prefix draft in sync when the board (or its saved prefix) changes, or the modal opens.
  useEffect(() => {
    setPrefix(currentBoard?.key_prefix ?? '');
  }, [currentBoard?.id, currentBoard?.key_prefix, open]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Recolour/rename omit position, so they never write back a stale one over a concurrent reorder.
  const rename = async (s: Status, label: string) => {
    const next = label.trim();
    if (!next || next === s.label) return;
    setBusy(true);
    try {
      await api.updateStatus(s.id, { label: next, color: s.color });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const recolor = async (s: Status, color: string) => {
    if (color === s.color) return;
    setBusy(true);
    try {
      await api.updateStatus(s.id, { label: s.label, color });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    const label = newLabel.trim();
    if (!label || !currentBoard || addingRef.current) return;
    addingRef.current = true;
    setBusy(true);
    try {
      const color = STATUS_PALETTE[statuses.length % STATUS_PALETTE.length];
      await api.createStatus(currentBoard.id, { label, color });
      setNewLabel('');
      await reload();
    } finally {
      addingRef.current = false;
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await api.deleteStatus(pendingDelete.id);
      setPendingDelete(null);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  // Drop → give the moved status the midpoint position between its new neighbours (one write),
  // mirroring how items reorder. `reload` then re-sorts from server truth.
  const onDragEnd = async (e: DragEndEvent) => {
    setActiveId(null);
    const overId = e.over ? String(e.over.id) : null;
    const aId = String(e.active.id);
    if (!overId || overId === aId) return;
    const from = statuses.findIndex((s) => s.id === aId);
    const to = statuses.findIndex((s) => s.id === overId);
    if (from === -1 || to === -1) return;
    const reordered = arrayMove(statuses, from, to);
    const moved = statuses[from];
    const prev = reordered[to - 1];
    const nxt = reordered[to + 1];
    let position: number;
    if (!prev && !nxt) position = 0;
    else if (!prev) position = nxt.position - 1;
    else if (!nxt) position = prev.position + 1;
    else position = (prev.position + nxt.position) / 2;
    setBusy(true);
    try {
      await api.updateStatus(moved.id, { label: moved.label, color: moved.color, position });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  // Where a deleted status's items land, mirroring the server (Not Started if it survives, else
  // the first remaining column). Delete is disabled for the last status, so a target always exists.
  const target = pendingDelete
    ? (() => {
        const remaining = statuses.filter((s) => s.id !== pendingDelete.id);
        return remaining.find((s) => s.value === 'not_started') ?? remaining[0];
      })()
    : undefined;

  return (
    <>
      <Modal open={open} onClose={onClose} title="Board settings" className="max-w-[560px]">
        {currentBoard && wc ? (
          <section className="mb-6 flex items-center justify-between gap-4 border-b border-line pb-6">
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-fg-strong">Watch this board</div>
              <p className="mt-0.5 text-[12px] text-fg-faint">Get activity + digest updates for every item on this board.</p>
            </div>
            <button
              type="button"
              onClick={() => wc.toggle('board', currentBoard.id)}
              aria-pressed={watching}
              className={cn(
                'inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-colors',
                watching ? 'bg-accent/15 text-accent' : 'bg-layer text-fg-soft hover:bg-layer-strong',
              )}
            >
              <Eye className="h-4 w-4" />
              {watching ? 'Watching' : 'Watch board'}
            </button>
          </section>
        ) : null}
        {currentBoard ? <BoardAccessSection boardId={currentBoard.id} /> : null}
        <section className="mb-6 border-b border-line pb-6">
          <div className="mb-1 text-[13px] font-semibold text-fg-strong">Item key prefix</div>
          <p className="mb-3 text-[12px] text-fg-faint">
            The short project code in front of every item key (JIRA-style). Item keys are{' '}
            <span className="font-semibold text-fg-muted">{normPrefix(prefix) || 'S'}-123</span>. Changing it
            re-labels every item on this board.
          </p>
          <div className="flex items-center gap-2">
            <input
              className={cn(inputCls, 'h-9 w-40 font-semibold uppercase tracking-wide')}
              placeholder="PB"
              value={prefix}
              maxLength={10}
              onChange={(e) => setPrefix(normPrefix(e.target.value))}
              onBlur={savePrefix}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') {
                  setPrefix(currentBoard?.key_prefix ?? '');
                  (e.target as HTMLInputElement).blur();
                }
              }}
              disabled={busy}
              aria-label="Item key prefix"
            />
            <span className="item-key rounded-md px-1.5 py-1 tnum text-[12px] font-semibold">
              {(normPrefix(prefix) || 'S') + '-42'}
            </span>
          </div>
        </section>

        <section>
          <div className="mb-1 text-[13px] font-semibold text-fg-strong">Statuses</div>
          <p className="mb-3 text-[12px] text-fg-faint">
            The columns of your kanban board. Drag to reorder, click a swatch to recolour, or rename inline.
            Deleting a status moves its items to another column.
          </p>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))}
            onDragCancel={() => setActiveId(null)}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={statuses.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {statuses.map((s) => (
                  <StatusRow
                    key={s.id}
                    status={s}
                    dragging={activeId === s.id}
                    canDelete={statuses.length > 1}
                    busy={busy}
                    onRename={(label) => rename(s, label)}
                    onRecolor={(color) => recolor(s, color)}
                    onDelete={() => setPendingDelete(s)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <div className="mt-3 flex items-center gap-2 border-t border-line pt-3">
            <input
              className={inputCls}
              placeholder="New status name"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && add()}
            />
            <Button variant="primary" disabled={busy || !newLabel.trim()} onClick={add}>
              Add
            </Button>
          </div>
        </section>
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={doDelete}
        title="Delete status?"
        message={
          pendingDelete && target ? (
            <>
              Delete the <b className="text-fg-strong">{pendingDelete.label}</b> status? Any items in it will move to{' '}
              <b className="text-fg-strong">{target.label}</b>.
            </>
          ) : undefined
        }
        confirmLabel="Delete status"
        busy={busy}
      />
    </>
  );
}

/** One draggable status row in the settings list: grip · colour swatch · name · delete. */
function StatusRow(props: {
  status: Status;
  dragging: boolean;
  canDelete: boolean;
  busy: boolean;
  onRename: (label: string) => void;
  onRecolor: (color: string) => void;
  onDelete: () => void;
}) {
  const { status } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: status.id });
  const style: CSSProperties = { transform: CSS.Translate.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 rounded-lg border border-line bg-surface-inset px-2 py-1.5',
        isDragging && 'opacity-60',
      )}
    >
      <span
        {...attributes}
        {...listeners}
        className="flex h-8 w-6 shrink-0 cursor-grab touch-none items-center justify-center rounded text-fg-ghost hover:text-fg-soft active:cursor-grabbing"
        aria-label="Drag to reorder"
        title="Drag to reorder"
      >
        <Grip />
      </span>
      <ColorPicker
        value={status.color}
        onChange={props.onRecolor}
        trigger={({ toggle }) => (
          <button
            type="button"
            onClick={toggle}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-transform hover:scale-110"
            aria-label={`Change ${status.label} colour`}
            title="Change colour"
          >
            <span className="h-3.5 w-3.5 rounded-full ring-1 ring-inset ring-black/25" style={{ background: status.color }} />
          </button>
        )}
      />
      <input
        className={cn(inputCls, 'h-9 flex-1')}
        defaultValue={status.label}
        onBlur={(e) => props.onRename(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            (e.target as HTMLInputElement).value = status.label;
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
      <button
        type="button"
        onClick={props.onDelete}
        disabled={!props.canDelete || props.busy}
        title={props.canDelete ? 'Delete status' : 'A board must keep at least one status'}
        aria-label={`Delete ${status.label}`}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-fg-faint transition-colors hover:bg-layer-strong hover:text-danger disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-fg-faint"
      >
        <Trash className="h-4 w-4" />
      </button>
    </div>
  );
}

export default BoardSettingsModal;
